import {
    AnvilSelect,
    AnvilSelectOptionsProps,
    Button,
    ButtonGroup,
    Icon,
    Stack,
} from '@servicetitan/design-system';
import classNames from 'classnames';
import { FC, useCallback, useState } from 'react';
import * as Styles from './zoom.module.less';

const zoomValues = [
    0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5,
] as const;

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export type ZoomValues = (typeof zoomValues)[number] | number;

export interface ZoomProps {
    value: ZoomValues;
    onChange: (value: ZoomValues) => void;
    onFitToWidth?: () => void;
    onFitToHeight?: () => void;
}

export const Zoom: FC<ZoomProps> = props => {
    const { onChange, onFitToHeight, onFitToWidth, value } = props;
    const [fitMode, setFitMode] = useState<'width' | 'height'>('width');

    const zoomInDisabled = value >= zoomValues[zoomValues.length - 1];
    const zoomOutDisabled = value <= zoomValues[0];

    const options = zoomValues.map(
        v =>
            ({
                text: `${(v * 100).toFixed(0)}%`,
                value: v,
            }) as AnvilSelectOptionsProps
    );

    if (!zoomValues.includes(value as never)) {
        options.push({
            text: `${(value * 100).toFixed(0)}%`,
            value,
        } as AnvilSelectOptionsProps);
    }

    const handleChange = (anvilProps: AnvilSelectOptionsProps | AnvilSelectOptionsProps[]) => {
        onChange((anvilProps as AnvilSelectOptionsProps).value);
    };

    const zoomOut = () => {
        const index = zoomValues.indexOf(value as never);
        if (index > 0) {
            onChange(zoomValues[index - 1]);
        } else if (index === -1) {
            // find closest value in zoomValues
            let curValue = zoomValues[0] as number;
            for (const item of zoomValues) {
                if (item < value) {
                    curValue = item;
                } else {
                    break;
                }
            }
            onChange(curValue);
        }
    };

    const zoomIn = () => {
        const index = zoomValues.indexOf(value as never);
        if (index !== -1 && index < zoomValues.length - 1) {
            onChange(zoomValues[index + 1]);
        } else if (index === -1) {
            // find closest value in zoomValues
            let curValue = zoomValues[0] as number;
            for (const item of zoomValues) {
                if (item > value) {
                    curValue = item;
                    break;
                }
            }
            onChange(curValue);
        }
    };

    const handleFit = useCallback(() => {
        if (fitMode === 'width') {
            onFitToWidth?.();
        } else {
            onFitToHeight?.();
        }
        setFitMode(fitMode === 'width' ? 'height' : 'width');
    }, [fitMode, onFitToHeight, onFitToWidth]);

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <ButtonGroup attached>
                <Button
                    small
                    outline
                    fill="subtle"
                    disabled={zoomOutDisabled}
                    icon={<Icon name="zoom_out" />}
                    onClick={zoomOut}
                    aria-label="Zoom Out"
                />
                <Button
                    small
                    outline
                    fill="subtle"
                    disabled={zoomInDisabled}
                    icon={<Icon name="zoom_in" />}
                    onClick={zoomIn}
                    aria-label="Zoom In"
                />
            </ButtonGroup>
            <AnvilSelect
                options={options}
                value={value}
                trigger={{ clearable: false, size: 'small', className: Styles.zoomInput }}
                onChange={handleChange}
                scrollHeight="350px"
            />
            <ButtonGroup attached>
                <Button
                    small
                    outline
                    fill="subtle"
                    icon={
                        <Icon
                            className={classNames({ [Styles.zoomIcon]: fitMode === 'width' })}
                            name="unfold_more"
                        />
                    }
                    onClick={handleFit}
                    aria-label="Fit"
                />
            </ButtonGroup>
        </Stack>
    );
};
